import { EventStatus, IEvent } from '@interfaces/event';
import { IEventChangedPayload } from '@interfaces/webSocketConnectionInfo';

import { env } from 'env';

const MINUTE_IN_MS = 60 * 1000;

// Long enough to cover a refetch plus DynamoDB replication lag, short enough
// that a missed follow-up message cannot hide a card indefinitely.
const OVERLAY_TTL_MS = 30 * 1000;

export interface IAppliedEventChange {
  status: EventStatus;
  timestamp: number;
  appliedAt: number;
}

const { REACT_APP_EVENT_CAROUSEL_HIDE_EXTERNAL_EVENT_TAGS } = env;

const hideEventTags = (REACT_APP_EVENT_CAROUSEL_HIDE_EXTERNAL_EVENT_TAGS || '')
  .split(',')
  .map((i) => i.trim());

export const filterVisibleEvents = (events: IEvent[]) => {
  if (hideEventTags.length > 0) {
    // Filter out external events
    return events.filter(({ eventTags }) => {
      if (eventTags) {
        return (
          eventTags.filter((tag) => !hideEventTags.includes(tag)).length ===
          eventTags.length
        );
      }
      return true;
    });
  }

  return events;
};

export const isExternalEvent = (event: IEvent) =>
  event?.eventTags && event?.eventTags.indexOf('External') > -1;

/**
 * Whether a status can put an experience in the carousel.
 *
 * @param status - Status to test.
 * @returns True for the statuses the carousel lists.
 */
export const isListableStatus = (status: EventStatus): boolean =>
  [EventStatus.ACTIVE, EventStatus.SCHEDULED].includes(status);

/**
 * Whether an experience belongs in the carousel at all.
 *
 * Shared by the fetch filter and the websocket handler so a card cannot survive
 * one path and be dropped by the other.
 *
 * @param event - Status and end time of the experience.
 * @returns True when the card should be listed.
 */
export const isVisibleInCarousel = ({
  status,
  endTime,
}: Pick<IEvent, 'status' | 'endTime'>): boolean =>
  isListableStatus(status) &&
  (endTime === 0 || endTime * 1000 > Date.now() + MINUTE_IN_MS);

/**
 * Layers the statuses pushed by `EVENT_CHANGED` over a freshly fetched list.
 *
 * The list is read with an eventually-consistent BatchGet, so a fetch issued
 * right after a broadcast can still answer with the pre-write item and undo what
 * the broadcast already showed. Entries older than the overlay window are
 * ignored, so a status the hub never heard about again cannot be pinned forever.
 *
 * @param events - Experiences as returned by the fetch.
 * @param applied - Last applied change per event id.
 * @returns The list with newer known statuses layered on top.
 */
export const overlayAppliedStatuses = (
  events: IEvent[],
  applied: Record<string, IAppliedEventChange>,
): IEvent[] =>
  events.map((event) => {
    const change = applied[event.eventId];

    if (
      !change ||
      change.status === event.status ||
      Date.now() - change.appliedAt > OVERLAY_TTL_MS
    ) {
      return event;
    }

    return {
      ...event,
      status: change.status,
      isDisabled: change.status !== EventStatus.ACTIVE,
    };
  });

/**
 * Drops overlay entries that are past the window, so the map does not grow for
 * the lifetime of the session — every experience on the environment is
 * broadcast to every hub client.
 *
 * @param applied - Last applied change per event id.
 * @returns The entries still inside the overlay window.
 */
export const pruneAppliedChanges = (
  applied: Record<string, IAppliedEventChange>,
): Record<string, IAppliedEventChange> =>
  Object.fromEntries(
    Object.entries(applied).filter(
      ([, change]) => Date.now() - change.appliedAt <= OVERLAY_TTL_MS,
    ),
  );

/**
 * Whether an `EVENT_CHANGED` broadcast concerns the project this hub serves.
 *
 * The backend broadcasts to every WEBAPP connection on the environment, so a
 * publish in a neighbouring project arrives here too. Since MV-1930 the
 * carousel only ever lists this project's experiences, so such a message can
 * at best cost a pointless refetch of `GET /api/v1/profile/events` — one per
 * publish, on every client of every other project.
 *
 * @param payload - Broadcast to test.
 * @param worldId - Project the hub serves; when it (or the payload's own
 * project) is unknown the message is kept, since dropping it would also drop
 * our own updates.
 * @returns True when the broadcast should be applied.
 */
export const isEventChangeForCurrentWorld = (
  payload: Pick<IEventChangedPayload, 'worldId'>,
  worldId: string | undefined,
): boolean => {
  if (!worldId || !payload.worldId) {
    return true;
  }

  return payload.worldId === worldId;
};

/**
 * Applies an `EVENT_CHANGED` broadcast to the loaded carousel.
 *
 * Only the allow-listed fields are applied; `needsRefetch` reports whether the
 * rest of the card (name, images, release version) still has to be fetched.
 *
 * @param events - Currently rendered experiences.
 * @param payload - The broadcast to apply.
 * @returns The next list and whether a refetch is still required.
 */
export const applyEventChanged = (
  events: IEvent[],
  payload: IEventChangedPayload,
): { events: IEvent[]; needsRefetch: boolean } => {
  const { eventId, event: changedFields } = payload;
  const listed = events.find((event) => event.eventId === eventId);

  if (!listed) {
    // An experience the participant just gained access to, or one that became
    // visible, needs the full record the message lacks. Anything that cannot be
    // listed at all is another project's business: every hub client receives
    // every experience's changes, and a refetch here would achieve nothing.
    return {
      events,
      needsRefetch: isListableStatus(changedFields.status),
    };
  }

  if (
    !isVisibleInCarousel({
      status: changedFields.status,
      endTime: listed.endTime,
    })
  ) {
    return {
      events: events.filter((event) => event.eventId !== eventId),
      needsRefetch: false,
    };
  }

  return {
    events: events.map((event) =>
      event.eventId === eventId
        ? {
            ...event,
            status: changedFields.status,
            // Mirrors what GET /api/v1/profile/events computes: only an ACTIVE
            // experience is enterable, the rest render as greyed-out cards.
            isDisabled: changedFields.status !== EventStatus.ACTIVE,
          }
        : event,
    ),
    needsRefetch: true,
  };
};
